import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Pause, Play, RotateCcw } from "lucide-react";
import { formatNumberFixed, formatSignedPercentage } from "../../../utils/numberUtils";
import type { MoveContext } from "../../../utils/strategyMoveContext";

interface Props {
  moveContext: MoveContext;
  dayLabel: string; // "today" or a formatted date
}

const NS = "http://www.w3.org/2000/svg";
const W = 520;
const H = 180;
const PAD = 22;
const BASE_Y = 138;
const TOP = 18;

function gaussian(z: number): number {
  return Math.exp(-0.5 * z * z);
}
// stable pseudo-jitter from an index (no Math.random)
function jitter(i: number): number {
  const x = Math.sin((i + 1) * 91.7) * 43758.5453;
  return x - Math.floor(x);
}
// Round SVG coordinates to 1dp (avoids .toFixed per repo lint rule).
function coord(n: number): number {
  return Math.round(n * 10) / 10;
}
function el(tag: string, attrs: Record<string, string | number>): SVGElement {
  const n = document.createElementNS(NS, tag);
  for (const k in attrs) n.setAttribute(k, String(attrs[k]));
  return n as SVGElement;
}

export default function StrategyMoveContextExplainer({ moveContext, dayLabel }: Props) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const timerRef = useRef<number | null>(null);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);

  const te = moveContext.trackingErrorPct ?? 0;
  const active = moveContext.activeDayPct;
  const strat = moveContext.strategyDayPct;
  const bmk = moveContext.benchmarkDayPct;

  // Sample the baseline to a manageable cloud.
  const cloud = useMemo(() => {
    const arr = moveContext.activeReturns;
    if (arr.length <= 90) return arr.map((d) => d.activePct);
    const step = Math.ceil(arr.length / 90);
    return arr.filter((_, i) => i % step === 0).map((d) => d.activePct);
  }, [moveContext.activeReturns]);

  const drawScene = useMemo(() => {
    return (teScale: number, opts: { withToday: boolean; scale: number }) => {
      const svg = svgRef.current;
      if (!svg || !(teScale > 0)) return;
      while (svg.firstChild) svg.removeChild(svg.firstChild);
      const xMin = -3.3 * teScale;
      const xMax = 3.3 * teScale;
      const sx = (x: number) =>
        PAD + ((Math.max(xMin, Math.min(xMax, x)) - xMin) / (xMax - xMin)) * (W - 2 * PAD);
      const sy = (v: number) => BASE_Y - v * (BASE_Y - TOP);
      const f = (x: number) => gaussian(x / teScale);

      let band = `M ${sx(-teScale)} ${BASE_Y}`;
      for (let x = -teScale; x <= teScale + 1e-6; x += teScale / 14)
        band += ` L ${coord(sx(x))} ${coord(sy(f(x)))}`;
      band += ` L ${sx(teScale)} ${BASE_Y} Z`;
      svg.appendChild(el("path", { d: band, class: "fill-green-500/15" }));

      let curve = "";
      for (let x = xMin; x <= xMax + 1e-6; x += (xMax - xMin) / 130)
        curve += (curve ? " L" : "M") + ` ${coord(sx(x))} ${coord(sy(f(x)))}`;
      svg.appendChild(el("path", { d: curve, fill: "none", class: "stroke-gray-400 dark:stroke-gray-500", "stroke-width": 1.5 }));
      svg.appendChild(el("line", { x1: PAD, x2: W - PAD, y1: BASE_Y, y2: BASE_Y, class: "stroke-gray-200 dark:stroke-gray-700" }));

      // dots fly in from the centre
      cloud.forEach((v, i) => {
        const sv = v * opts.scale;
        const tx = sx(sv);
        const tyTop = sy(f(sv));
        const ty = BASE_Y - jitter(i) * (BASE_Y - tyTop);
        const c = el("circle", { cx: W / 2, cy: BASE_Y, r: 2.6, class: "fill-gray-400 dark:fill-gray-500", opacity: 0 }) as SVGCircleElement;
        c.style.transition = "cx .7s cubic-bezier(.3,.7,.3,1), cy .7s cubic-bezier(.3,.7,.3,1), opacity .4s";
        c.style.transitionDelay = `${i * 6}ms`;
        svg.appendChild(c);
        requestAnimationFrame(() => {
          c.setAttribute("cx", String(coord(tx)));
          c.setAttribute("cy", String(coord(ty)));
          c.setAttribute("opacity", "0.5");
        });
      });

      if (opts.withToday && active != null) {
        const tx = sx(active);
        const ty = sy(f(active));
        svg.appendChild(el("line", { x1: tx, x2: tx, y1: ty, y2: BASE_Y, class: "stroke-green-500", "stroke-width": 2, "stroke-dasharray": "3 3", opacity: 0.9 }));
        const dot = el("circle", { cx: tx, cy: -10, r: 6, class: "fill-green-500 stroke-white dark:stroke-black", "stroke-width": 2 }) as SVGCircleElement;
        dot.style.transition = "cy .6s cubic-bezier(.34,1.2,.64,1)";
        svg.appendChild(dot);
        requestAnimationFrame(() => dot.setAttribute("cy", String(coord(ty))));
        const t = el("text", { x: tx, y: ty - 10, "text-anchor": "middle", class: "fill-green-600 dark:fill-green-500", "font-size": 10, "font-weight": 700, "font-family": "ui-monospace, monospace" });
        t.textContent = dayLabel;
        svg.appendChild(t);
      }
    };
  }, [cloud, active, dayLabel]);

  // Beats: caption + a scene renderer.
  const beats = useMemo(() => {
    const teLabel = `±${formatNumberFixed(te, 1)}%`;
    const dayPhrase = dayLabel === "today" ? "today" : `on ${dayLabel}`;
    const clearSvg = () => {
      const s = svgRef.current;
      if (s) while (s.firstChild) s.removeChild(s.firstChild);
    };

    // Missing-data path: the baseline is valid (we can show the normal range),
    // but there is no comparable gap for the focus day (benchmark not in, or no
    // strategy value for that day). Never fabricate a 0.0% gap or "normal day".
    if (active == null) {
      return [
        {
          cap:
            strat != null ? (
              <>
                Your strategy is{" "}
                <b className="text-gray-900 dark:text-white">{formatSignedPercentage(strat, 1)}</b> {dayPhrase}.
                The benchmark isn&apos;t in yet, so we can&apos;t size the gap.
              </>
            ) : (
              <>No comparable move for {dayLabel} yet.</>
            ),
          run: clearSvg,
        },
        {
          cap: (
            <>
              Here&apos;s the strategy&apos;s <b className="text-gray-900 dark:text-white">normal daily range</b> vs the
              benchmark — about {teLabel}. We&apos;ll place {dayLabel}&apos;s gap once the benchmark is in.
            </>
          ),
          run: () => drawScene(te, { withToday: false, scale: 1 }),
        },
      ];
    }

    // active != null implies both legs are present (computeMoveContext contract).
    const within = te > 0 ? Math.abs(active / te) < 1 : true;
    const list: Array<{ cap: React.ReactNode; run: () => void }> = [
      {
        cap: <>Your strategy is <b className="text-red-500">{formatSignedPercentage(strat ?? 0, 1)}</b> {dayPhrase}. Stomach-drop.</>,
        run: clearSvg,
      },
      {
        cap: <>But the market moved too ({formatSignedPercentage(bmk ?? 0, 1)}). The part that&apos;s <b className="text-rp-amber">just your strategy</b> is {formatSignedPercentage(active, 1)}.</>,
        run: clearSvg,
      },
      {
        cap: <>Here&apos;s <b className="text-gray-900 dark:text-white">every day your strategy has had</b> vs the benchmark — its normal range, about {teLabel}.</>,
        run: () => drawScene(te, { withToday: false, scale: 1 }),
      },
      {
        cap: <><b className="text-gray-900 dark:text-white">{dayLabel === "today" ? "Today" : dayLabel}</b>&apos;s gap lands {within ? <b className="text-green-500">right in the crowd</b> : <b className="text-rp-amber">out toward the edge</b>}.</>,
        run: () => drawScene(te, { withToday: true, scale: 1 }),
      },
      {
        cap: <>Same gap, <b className="text-gray-900 dark:text-white">steadier</b> strategy: it would sit near the edge — a bigger deal.</>,
        run: () => drawScene(te * 0.45, { withToday: true, scale: 0.45 }),
      },
      {
        cap: <>Same gap, <b className="text-gray-900 dark:text-white">jumpier</b> strategy: barely a blip. We always measure against <b className="text-gray-900 dark:text-white">your</b> normal — not the market&apos;s.</>,
        run: () => drawScene(te * 2.2, { withToday: true, scale: 2.2 }),
      },
      {
        cap: <>For <b className="text-gray-900 dark:text-white">your</b> strategy: {within ? "a normal day. Breathe." : "a wider-than-usual day — worth a look."}</>,
        run: () => drawScene(te, { withToday: true, scale: 1 }),
      },
    ];
    return list;
  }, [te, active, strat, bmk, dayLabel, drawScene]);

  // Run the current beat whenever it changes.
  useEffect(() => {
    beats[idx]?.run();
  }, [idx, beats]);

  // Auto-advance while playing.
  useEffect(() => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    if (!playing) return;
    if (idx >= beats.length - 1) {
      setPlaying(false);
      return;
    }
    const dwell = idx < 2 ? 1900 : 2500;
    timerRef.current = window.setTimeout(() => setIdx((i) => Math.min(i + 1, beats.length - 1)), dwell);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [playing, idx, beats.length]);

  const toggle = () => {
    if (!playing && idx >= beats.length - 1) {
      setIdx(0);
      setPlaying(true);
    } else {
      setPlaying((p) => !p);
    }
  };
  const step = (d: number) => {
    setPlaying(false);
    setIdx((i) => Math.max(0, Math.min(beats.length - 1, i + d)));
  };
  const replay = () => {
    setIdx(0);
    setPlaying(true);
  };

  return (
    <div>
      <div className="mb-2 flex gap-1">
        {beats.map((_, i) => (
          <span key={i} className={`h-1 flex-1 rounded-full ${i <= idx ? "bg-rp-amber" : "bg-gray-200 dark:bg-gray-700"}`} />
        ))}
      </div>
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="How the move-context score works" />
      <p className="mt-2 min-h-[2.6rem] text-sm leading-snug text-gray-600 dark:text-gray-300">{beats[idx]?.cap}</p>
      <div className="mt-2 flex items-center gap-2">
        <button type="button" onClick={() => step(-1)} aria-label="Previous" className="rounded-lg border border-gray-200 dark:border-rp-separator p-1.5 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button type="button" onClick={toggle} className="flex items-center gap-1.5 rounded-lg bg-rp-amber px-3 py-1.5 text-sm font-semibold text-white hover:bg-yellow-600 transition-colors">
          {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          {playing ? "Pause" : idx >= beats.length - 1 ? "Replay" : "Play"}
        </button>
        <button type="button" onClick={() => step(1)} aria-label="Next" className="rounded-lg border border-gray-200 dark:border-rp-separator p-1.5 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600">
          <ChevronRight className="h-4 w-4" />
        </button>
        <button type="button" onClick={replay} aria-label="Replay" className="ml-auto rounded-lg border border-gray-200 dark:border-rp-separator p-1.5 text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600">
          <RotateCcw className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
